import { useEffect, useRef, useCallback } from 'react';
import { postActivity } from '../api';

/**
 * Tracks mouse movement, keyboard events, and page visibility.
 * Calculates an activity score (0-100) over the last `windowMs` milliseconds.
 * Auto-posts idle/active events to the API.
 *
 * Returns: { getActivityScore }
 */
export default function useActivityMonitor({ employeeId, timeEntryId, enabled = true, idleThresholdMs = 120000 }) {
  const eventsRef = useRef([]); // timestamps of activity events
  const lastActiveRef = useRef(Date.now());
  const isIdleRef = useRef(false);
  const employeeIdRef = useRef(employeeId);
  const timeEntryIdRef = useRef(timeEntryId);

  // Keep refs updated without re-registering listeners
  useEffect(() => { employeeIdRef.current = employeeId; }, [employeeId]);
  useEffect(() => { timeEntryIdRef.current = timeEntryId; }, [timeEntryId]);

  const recordEvent = useCallback(() => {
    const now = Date.now();
    eventsRef.current.push(now);
    // Keep only events from last 5 minutes
    eventsRef.current = eventsRef.current.filter(t => now - t < 300000);
    lastActiveRef.current = now;

    // If was idle, log active event
    if (isIdleRef.current && employeeIdRef.current) {
      isIdleRef.current = false;
      postActivity({ employee_id: employeeIdRef.current, time_entry_id: timeEntryIdRef.current || null, event_type: 'active', description: 'Resumed activity' }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    // Throttle to max 1 event per second to avoid flooding
    let lastRecorded = 0;
    const throttled = () => {
      const now = Date.now();
      if (now - lastRecorded > 1000) { lastRecorded = now; recordEvent(); }
    };

    events.forEach(ev => window.addEventListener(ev, throttled, { passive: true }));

    // Page visibility change
    const onVisibility = () => {
      if (!document.hidden) recordEvent();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Idle checker every 30s
    const idleTimer = setInterval(() => {
      if (!employeeIdRef.current) return;
      const idle = Date.now() - lastActiveRef.current > idleThresholdMs;
      if (idle && !isIdleRef.current) {
        isIdleRef.current = true;
        const idleMins = Math.round(idleThresholdMs / 60000);
        postActivity({ employee_id: employeeIdRef.current, time_entry_id: timeEntryIdRef.current || null, event_type: 'idle', description: `No activity detected for ${idleMins}+ minutes` }).catch(() => {});
      }
    }, 30000);

    return () => {
      events.forEach(ev => window.removeEventListener(ev, throttled));
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(idleTimer);
    };
  }, [enabled, idleThresholdMs, recordEvent]);

  // Returns 0-100 score based on events in the last windowMs
  const getActivityScore = useCallback((windowMs = 300000) => {
    const now = Date.now();
    const recent = eventsRef.current.filter(t => now - t < windowMs);
    // Normalize: 60+ events in window = 100%, scale linearly
    return Math.min(100, Math.round((recent.length / 60) * 100));
  }, []);

  return { getActivityScore };
}
